'use client';

import { useState } from 'react';

const INITIAL_STATE = {
  balance: 5000,
  apr: 18,
  monthlyPayment: 150,
};

const PRESETS = {
  conservative: {
    balance: 3000,
    apr: 15,
    monthlyPayment: 200,
  },
  moderate: {
    balance: 5000,
    apr: 18,
    monthlyPayment: 150,
  },
  aggressive: {
    balance: 10000,
    apr: 22,
    monthlyPayment: 100,
  },
};

export default function CalculatorCreditCard() {
  const [balance, setBalance] = useState(INITIAL_STATE.balance);
  const [apr, setApr] = useState(INITIAL_STATE.apr);
  const [monthlyPayment, setMonthlyPayment] = useState(INITIAL_STATE.monthlyPayment);

  const handleReset = () => {
    setBalance(INITIAL_STATE.balance);
    setApr(INITIAL_STATE.apr);
    setMonthlyPayment(INITIAL_STATE.monthlyPayment);
  };

  const handlePreset = (preset: keyof typeof PRESETS) => {
    const values = PRESETS[preset];
    setBalance(values.balance);
    setApr(values.apr);
    setMonthlyPayment(values.monthlyPayment);
  };

  const calculate = () => {
    const monthlyRate = apr / 100 / 12;
    let remainingBalance = balance;
    let months = 0;
    let totalInterest = 0;

    while (remainingBalance > 0 && months < 600) {
      const interest = remainingBalance * monthlyRate;
      totalInterest += interest;
      remainingBalance = remainingBalance + interest - monthlyPayment;
      months++;
      if (monthlyPayment <= interest) break;
    }

    const payoffYears = Math.floor(months / 12);
    const payoffMonths = months % 12;
    const totalPaid = balance + totalInterest;

    return {
      months,
      payoffYears,
      payoffMonths,
      totalInterest,
      totalPaid,
    };
  };

  const results = calculate();

  return (
    <div className="grid lg:grid-cols-5 gap-8">
      <div className="lg:col-span-2">
        <div className="bg-white rounded-3xl p-8 shadow-modern-xl border border-slate-200 sticky top-24">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-2 h-8 bg-gradient-to-b from-red-500 to-orange-600 rounded-full"></div>
            <h2 className="text-2xl font-bold text-slate-900">Debt Details</h2>
          </div>

          {/* Preset and Reset Buttons */}
          <div className="mb-6 space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => handlePreset('conservative')}
                className="px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg font-semibold text-sm transition-all border border-blue-200"
              >
                Conservative
              </button>
              <button
                onClick={() => handlePreset('moderate')}
                className="px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg font-semibold text-sm transition-all border border-indigo-200"
              >
                Moderate
              </button>
              <button
                onClick={() => handlePreset('aggressive')}
                className="px-3 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg font-semibold text-sm transition-all border border-purple-200"
              >
                Aggressive
              </button>
            </div>
            <button
              onClick={handleReset}
              className="w-full px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-semibold text-sm transition-all border border-slate-300"
            >
              Reset to Default
            </button>
          </div>

          <div className="space-y-6">
            <InputField
              label="Current Balance"
              value={balance}
              onChange={(v) => setBalance(v)}
              min={0}
              prefix="$"
            />
            <InputField
              label="Interest Rate (APR)"
              value={apr}
              onChange={(v) => setApr(v)}
              min={0}
              max={35}
              step={0.01}
              unit="%"
            />
            <InputField
              label="Monthly Payment"
              value={monthlyPayment}
              onChange={(v) => setMonthlyPayment(v)}
              min={0}
              prefix="$"
            />
          </div>
        </div>
      </div>

      <div className="lg:col-span-3 space-y-6">
        <div className="bg-gradient-to-br from-red-500 via-orange-500 to-amber-600 rounded-3xl p-10 shadow-modern-xl text-white">
          <div className="text-sm font-semibold uppercase tracking-wider opacity-90 mb-3">
            Time to Pay Off
          </div>
          <div className="text-6xl font-bold mb-2">
            {results.payoffYears > 0
              ? `${results.payoffYears}y ${results.payoffMonths}m`
              : `${results.payoffMonths} months`}
          </div>
          <div className="text-lg opacity-90">
            {results.months} total months to become debt-free
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <ResultCard
            label="Total Interest Paid"
            value={`$${results.totalInterest.toLocaleString('en-US', { maximumFractionDigits: 0 })}`}
            description="Cost of carrying debt"
            gradient="from-purple-500 to-pink-600"
          />
          <ResultCard
            label="Total Amount Paid"
            value={`$${results.totalPaid.toLocaleString('en-US', { maximumFractionDigits: 0 })}`}
            description="Principal + interest"
            gradient="from-blue-500 to-indigo-600"
          />
        </div>

        <div className="bg-white rounded-3xl p-8 shadow-modern-xl border border-slate-200">
          <h3 className="text-xl font-bold text-slate-900 mb-6">Payoff Summary</h3>
          <div className="space-y-4">
            <InsightRow label="Current balance" value={`$${balance.toLocaleString('en-US')}`} />
            <InsightRow label="Monthly payment" value={`$${monthlyPayment.toLocaleString('en-US')}`} />
            <InsightRow label="Interest rate" value={`${apr}% APR`} />
            <InsightRow
              label="Interest as % of balance"
              value={`${((results.totalInterest / balance) * 100).toFixed(1)}%`}
            />
          </div>
        </div>

        {monthlyPayment <= (balance * (apr / 100 / 12)) && (
          <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-6">
            <div className="flex gap-3">
              <svg className="w-6 h-6 text-red-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <div className="font-bold text-red-900 mb-1">Warning: Payment Too Low</div>
                <div className="text-red-700 text-sm">
                  Your monthly payment is less than or equal to the monthly interest charge. You need to increase your payment to make progress on your debt.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function InputField({
  label,
  value,
  onChange,
  min = 0,
  max,
  step = 1,
  prefix,
  unit,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  prefix?: string;
  unit?: string;
}) {
  return (
    <div>
      <label className="block text-sm font-bold text-slate-700 mb-2">{label}</label>
      <div className="relative">
        {prefix && (
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">
            {prefix}
          </span>
        )}
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          min={min}
          max={max}
          step={step}
          className={`w-full ${prefix ? 'pl-8' : 'pl-4'} ${unit ? 'pr-16' : 'pr-4'} py-3.5 border-2 border-slate-200 rounded-xl focus:border-red-500 focus:ring-4 focus:ring-red-500/20 outline-none transition-all font-semibold text-slate-900 text-lg`}
        />
        {unit && (
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">
            {unit}
          </span>
        )}
      </div>
    </div>
  );
}

function ResultCard({
  label,
  value,
  description,
  gradient,
}: {
  label: string;
  value: string;
  description: string;
  gradient: string;
}) {
  return (
    <div className={`bg-gradient-to-br ${gradient} rounded-2xl p-6 shadow-modern-lg text-white`}>
      <div className="text-sm font-semibold uppercase tracking-wider opacity-90 mb-2">{label}</div>
      <div className="text-4xl font-bold mb-1">{value}</div>
      <div className="text-sm opacity-90">{description}</div>
    </div>
  );
}

function InsightRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between items-center py-3 border-b border-slate-100 last:border-0">
      <span className="text-slate-600 font-medium">{label}</span>
      <span className="text-slate-900 font-bold text-lg">{value}</span>
    </div>
  );
}